/**
 * ReferenceMediaPane — the RIGHT pane of the evaluation workspace (Phase 2).
 *
 * STAFF-facing, READ-ONLY view of the client's reference media (Photos /
 * Videos / Maps) for the property under evaluation. Live-subscribes via
 * `listenPropertyMedia` (Silas's FROZEN read contract) — no writes, no
 * uploads, no deploys. The write/ingest path (Mack) and the rules gate (Vex)
 * are owner-gated and OUT of this component's scope.
 *
 * Scope toggle: "This engagement" (default — only media captured for this VP###)
 * vs "Whole building" (this client's library across past engagements). The
 * clientId privacy wall is present in BOTH (enforced in the query spec).
 *
 * States handled (all calm, none crash the pane):
 *   loading  → skeleton in the active tab
 *   empty    → "No client media yet…" (the COMMON fresh-property case — NOT an error)
 *   denied   → "Media library not yet enabled — pending the security rollout"
 *              (expected until the owner-gated rules deploy)
 *   error    → non-destructive inline note + Retry (re-subscribes)
 *
 * TOKENS ONLY — styling lives in evaluate-workspace.css.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  listenPropertyMedia,
  type PropertyMedia,
} from '../../lib/propertyMedia';
import {
  type MediaScope,
  type MediaTab,
  MEDIA_TABS,
  splitByTab,
  sortMediaForDisplay,
  distinctFloors,
  filterMediaByTags,
  displayTime,
  sourceLabel,
  isVideo,
} from '../../lib/propertyMediaModel';
import {
  subscribeMarks,
  subscribeMarkups,
  type DispoItem,
  type MarkupItem,
} from '../../lib/evalDispositionBridge';
import { setAuoPortalHost } from '../../lib/evalAuoPortal';

type LoadState = 'loading' | 'ready' | 'denied' | 'error';

/**
 * The right pane has FOUR top-level tabs: the three media tabs PLUS the ONE
 * unified "AUO disposition" list (owner-confirmed 2026-07-01: doors + AUOs +
 * markups fold into a single numbered list — the separate Markups tab is gone,
 * markups are filterable within the one list). Switching tabs changes ONLY this
 * panel — the center map never changes.
 */
type RightTab = MediaTab | 'auo';
const RIGHT_TABS: readonly RightTab[] = [...MEDIA_TABS, 'auo'] as const;
const isMediaTab = (t: RightTab): t is MediaTab =>
  (MEDIA_TABS as readonly string[]).includes(t);

const TAB_LABELS: Record<RightTab, string> = {
  photos: 'Photos',
  videos: 'Videos',
  maps: 'Maps',
  auo: 'AUO disposition',
};

/** "12 Jun 2026" → "12 Jun". Tolerant of a bad / missing ISO string. */
function shortDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function TabIcon({ tab }: { tab: RightTab }) {
  if (tab === 'auo') {
    return (
      <svg className="evalws-ref-ic" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 11l3 3 8-8" />
        <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
      </svg>
    );
  }
  if (tab === 'videos') {
    return (
      <svg className="evalws-ref-ic" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="14" height="14" rx="2" />
        <path d="m17 9 4-2v10l-4-2" />
      </svg>
    );
  }
  if (tab === 'maps') {
    return (
      <svg className="evalws-ref-ic" viewBox="0 0 24 24" aria-hidden="true">
        <path d="m9 4 6 2 6-2v14l-6 2-6-2-6 2V6z" />
        <path d="M9 4v14" />
        <path d="M15 6v14" />
      </svg>
    );
  }
  return (
    <svg className="evalws-ref-ic" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.6" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

/** Small provenance + floor/place chips rendered from the item data. */
function ItemTags({ item }: { item: PropertyMedia }) {
  return (
    <div className="evalws-ref-tags">
      <span className="evalws-ref-srcchip">{sourceLabel(item.source)}</span>
      {item.floor ? (
        <span className="evalws-ref-tagchip">{item.floor}</span>
      ) : null}
      {item.place ? (
        <span className="evalws-ref-tagchip">{item.place}</span>
      ) : null}
    </div>
  );
}

function Thumb({
  item,
  selected,
  onSelect,
}: {
  item: PropertyMedia;
  selected: boolean;
  onSelect: () => void;
}) {
  const video = isVideo(item);
  const caption = item.label || (video ? 'Video' : 'Photo');
  const poster = item.thumbUrl ?? (video ? undefined : item.url);
  return (
    <button
      type="button"
      className={`evalws-thumb${selected ? ' is-sel' : ''}`}
      aria-pressed={selected}
      onClick={onSelect}
      title={caption}
    >
      <span className={`evalws-thumb-ph${video ? ' is-video' : ''}`}>
        {poster ? (
          <img src={poster} alt="" loading="lazy" decoding="async" />
        ) : (
          <TabIcon tab={video ? 'videos' : 'photos'} />
        )}
      </span>
      <span className="evalws-thumb-cap">{caption}</span>
    </button>
  );
}

/** The enlarged preview for the selected item (img, inline <video>, or map). */
function Preview({ item }: { item: PropertyMedia }) {
  const video = isVideo(item);
  const date = shortDate(displayTime(item));
  return (
    <div className="evalws-ref-preview">
      <div className="evalws-ref-stage">
        {video ? (
          <video
            className="evalws-ref-video"
            src={item.url}
            poster={item.thumbUrl}
            aria-label={item.label || 'Reference video'}
            controls
            playsInline
            preload="metadata"
          />
        ) : (
          <img
            className="evalws-ref-img"
            src={item.url}
            alt={item.label || 'Reference media'}
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      <div className="evalws-ref-cap">
        <b>{item.label || (video ? 'Video' : 'Photo')}</b>
        <span>
          {sourceLabel(item.source)}
          {date ? ` · ${date}` : ''}
        </span>
      </div>
      <ItemTags item={item} />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="evalws-ref-skel" aria-hidden="true">
      <div className="evalws-ref-skel-stage evalws-shimmer" />
      <div className="evalws-ref-skel-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="evalws-ref-skel-cell evalws-shimmer" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ tabLabel }: { tabLabel: string }) {
  return (
    <div className="evalws-ref-state">
      <svg className="evalws-ref-state-ic" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.6" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
      <b>No client media yet for this property</b>
      <p>
        No {tabLabel.toLowerCase()} on file yet. Photos, videos and maps land here as
        the client shares them.
      </p>
    </div>
  );
}

function DeniedState() {
  return (
    <div className="evalws-ref-state is-info">
      <svg className="evalws-ref-state-ic" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <path d="M12 8h.01" />
      </svg>
      <b>Media library not yet enabled</b>
      <p>
        Pending the security rollout. The reference library switches on for this
        property once the owner-gated access rules are deployed — this is
        expected, not an error.
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="evalws-ref-state is-warn" role="alert">
      <svg className="evalws-ref-state-ic" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
      <b>Couldn’t load reference media</b>
      <p>A temporary problem reaching the media library.</p>
      <button type="button" className="evalws-btn evalws-btn-sec" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

/**
 * AuoPortalMount — the target region for the REAL AuoReviewPanel.
 *
 * The workspace does NOT re-implement the AUO card. This mounts an empty <div>
 * and publishes its DOM node to the portal seam; StaffTechnicalPanel (already
 * mounted in the left MapPane) portals its live `AuoReviewPanel` — with all its
 * real props, state, admin gating, and the authoritative confirm path — INTO
 * this node. On unmount (tab switch away) it clears the host so the panel stops
 * portalling. One scroller (the right-pane body) — no nested scroll added here.
 */
function AuoPortalMount() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setAuoPortalHost(ref.current);
    return () => setAuoPortalHost(null);
  }, []);
  return <div className="evalws-auo-portal" ref={ref} />;
}

export default function ReferenceMediaPane({
  propertyId,
  projectId,
  clientId,
}: {
  propertyId: string;
  projectId: string;
  clientId: string | null;
}) {
  // scope is fixed to 'engagement' (#9 — the engagement/building toggle removed).
  const [scope] = useState<MediaScope>('engagement');
  const [items, setItems] = useState<PropertyMedia[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  // topTab spans all 5 right-pane tabs; `tab` keeps the active MEDIA sub-view so
  // returning to a media tab restores the user's last media selection.
  const [topTab, setTopTab] = useState<RightTab>('photos');
  const [tab, setTab] = useState<MediaTab>('photos');
  const [floorFilter, setFloorFilter] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // retry nonce — bumping it re-runs the subscribe effect.
  const [retryNonce, setRetryNonce] = useState(0);
  // disposition state from the marking-surface bridge (this pane mounts at load,
  // so it never misses the panel's initial emit; it feeds the tab bodies + counts).
  const [dispoItems, setDispoItems] = useState<DispoItem[]>([]);
  const [markupItems, setMarkupItems] = useState<MarkupItem[]>([]);
  // NOTE: the AUO tab hosts the REAL unified list (doors + AUOs + markups) via a
  // portal (see AuoPortalMount) — the palette, session context and selection are
  // consumed by StaffTechnicalPanel directly, not re-subscribed here. We keep the
  // marks + markups subscriptions ONLY to drive the tab badge total below.
  useEffect(() => subscribeMarks(setDispoItems), []);
  useEffect(() => subscribeMarkups(setMarkupItems), []);
  // the tab badge counts the TOTAL of the one unified list: doors + AUOs (in
  // dispoItems) + markups — matching everything the portalled list shows.
  const dispoTotal = dispoItems.length + markupItems.length;

  // ── live subscribe (re-subscribes on property/project/client/scope change) ──
  useEffect(() => {
    // No clientId → no privacy wall to query against. Treat as a calm empty
    // state rather than firing an unwalled query.
    if (!clientId) {
      setItems([]);
      setState('ready');
      return;
    }
    setState('loading');
    setItems([]);
    const unsub = listenPropertyMedia(
      { propertyId, clientId, projectId, scope },
      (next) => {
        setItems(sortMediaForDisplay(next));
        setState('ready');
      },
      (err) => {
        const code = (err as { code?: string }).code ?? '';
        const denied =
          code === 'permission-denied' || /permission|insufficient/i.test(err.message);
        setState(denied ? 'denied' : 'error');
      },
    );
    return unsub;
  }, [propertyId, projectId, clientId, scope, retryNonce]);

  const byTab = useMemo(() => splitByTab(items), [items]);
  const counts = useMemo(
    () => ({
      photos: byTab.photos.length,
      videos: byTab.videos.length,
      maps: byTab.maps.length,
    }),
    [byTab],
  );

  const tabItems = byTab[tab];
  const floors = useMemo(() => distinctFloors(tabItems), [tabItems]);
  const visible = useMemo(
    () => filterMediaByTags(tabItems, { floor: floorFilter ?? undefined }),
    [tabItems, floorFilter],
  );

  // keep the floor filter valid when the active tab / data changes.
  useEffect(() => {
    if (floorFilter && !floors.includes(floorFilter)) setFloorFilter(null);
  }, [floors, floorFilter]);

  const selected =
    visible.find((m) => m.mediaId === selectedId) ?? visible[0] ?? null;

  // Select a top tab; entering a media tab also restores that media sub-view.
  const selectTop = (t: RightTab) => {
    setTopTab(t);
    if (isMediaTab(t)) {
      setTab(t);
      setSelectedId(null);
    }
  };

  // Roving-tabindex keyboard nav across all 5 right-pane tabs (WCAG 2.2 / DS §2.4
  // — Arrow moves + selects, Home/End jump to the ends). Focus follows selection.
  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const i = RIGHT_TABS.indexOf(topTab);
    let ni = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') ni = (i + 1) % RIGHT_TABS.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      ni = (i - 1 + RIGHT_TABS.length) % RIGHT_TABS.length;
    else if (e.key === 'Home') ni = 0;
    else if (e.key === 'End') ni = RIGHT_TABS.length - 1;
    if (ni < 0) return;
    e.preventDefault();
    const nt = RIGHT_TABS[ni];
    selectTop(nt);
    requestAnimationFrame(() =>
      document.getElementById(`evalws-reftab-${nt}`)?.focus(),
    );
  };

  return (
    <aside className="evalws-refpane" aria-label="Client reference media">
      {/* ── record-tabs (§2.4) with live counts — 5 tabs: media + dispositions ── */}
      <div className="evalws-ref-tabs" role="tablist" aria-label="Reference and disposition">
        {RIGHT_TABS.map((t) => {
          const cnt = t === 'auo' ? dispoTotal : counts[t as MediaTab];
          return (
            <button
              key={t}
              type="button"
              role="tab"
              id={`evalws-reftab-${t}`}
              aria-selected={topTab === t}
              aria-controls="evalws-ref-panel"
              tabIndex={topTab === t ? 0 : -1}
              className="evalws-ref-tab"
              onKeyDown={onTabKeyDown}
              onClick={() => selectTop(t)}
            >
              <TabIcon tab={t} />
              {TAB_LABELS[t]}
              <span className="evalws-ref-cnt">{cnt}</span>
            </button>
          );
        })}
      </div>

      {/* scope toggle removed (#9 — "we don't need that now"). Media defaults to
          this engagement; `scope` stays 'engagement'. */}

      {/* ── panel ── */}
      <div
        className="evalws-ref-body"
        id="evalws-ref-panel"
        role="tabpanel"
        aria-labelledby={`evalws-reftab-${topTab}`}
        aria-busy={isMediaTab(topTab) && state === 'loading'}
      >
        {/* AUO disposition — hosts the REAL unified list (doors + AUOs + markups,
            one numbered sequence matching the map pins), portalled from the
            left-pane StaffTechnicalPanel. NOT a re-implementation: AUO rows render
            the REAL shared AuoCardBody with the authoritative confirm path. */}
        {topTab === 'auo' && <AuoPortalMount />}

        {isMediaTab(topTab) && state === 'loading' && <Skeleton />}
        {isMediaTab(topTab) && state === 'denied' && <DeniedState />}
        {isMediaTab(topTab) && state === 'error' && (
          <ErrorState onRetry={() => setRetryNonce((n) => n + 1)} />
        )}
        {isMediaTab(topTab) && state === 'ready' && tabItems.length === 0 && (
          <EmptyState tabLabel={TAB_LABELS[tab]} />
        )}

        {isMediaTab(topTab) && state === 'ready' && tabItems.length > 0 && (
          <>
            {/* floor/place filter chips (client-side secondary filter) */}
            {floors.length > 0 && (
              <div className="evalws-ref-chips" role="group" aria-label="Filter by floor">
                <button
                  type="button"
                  className="evalws-ref-chip"
                  aria-pressed={floorFilter === null}
                  onClick={() => setFloorFilter(null)}
                >
                  All
                </button>
                {floors.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className="evalws-ref-chip"
                    aria-pressed={floorFilter === f}
                    onClick={() => setFloorFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}

            {selected && <Preview item={selected} />}

            {visible.length > 0 ? (
              <>
                <div className="evalws-ref-gridlabel">
                  {visible.length} {TAB_LABELS[tab].toLowerCase()}
                </div>
                <div className="evalws-thumb-grid">
                  {visible.map((m) => (
                    <Thumb
                      key={m.mediaId}
                      item={m}
                      selected={selected?.mediaId === m.mediaId}
                      onSelect={() => setSelectedId(m.mediaId)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="evalws-ref-state">
                <b>No {TAB_LABELS[tab].toLowerCase()} on this floor</b>
                <p>Clear the floor filter to see the rest.</p>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
